import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCheck } from 'react-icons/fa';
import { getBrand } from '../utils/brandStyles';

/**
 * MWO Prototype — boutique digital transformation positioning.
 * Route: /preview/mwo  (not linked in nav)
 *
 * Replace current MWOHome content with this once approved.
 */

const BOOKING_URL = '#book'; // TODO: replace with Calendly URL when ready

const audiences = [
  {
    title: 'Scaling consultancies',
    size: '5–30 people',
    body: "You've outgrown personal Gmail and Dropbox. You need real IT before clients start to notice."
  },
  {
    title: 'Established agencies',
    size: '30–100 people',
    body: "Your stack works but it's bloated and slow. You need someone to take it from chaotic to operational."
  },
  {
    title: 'Law firms modernizing',
    size: 'any size',
    body: "You need Microsoft 365, security compliance, and document workflows that don't break the rules."
  }
];

const sprints = [
  {
    name: '30-day Quick Start',
    price: '€8,000',
    for: 'Solo founders and 5–15 person teams that just need the basics done right.',
    includes: [
      'Microsoft 365 or Google Workspace migration',
      'MFA + endpoint security baseline',
      'One AI workflow (e.g. email triage)',
      '30-day post-launch support'
    ],
    featured: false
  },
  {
    name: '60-day Modernize',
    price: '€16,000',
    for: '15–50 person firms with bloated stacks that don’t talk to each other.',
    includes: [
      'Everything in Quick Start',
      'Cloud migration (on-prem & legacy SaaS)',
      'Identity-as-a-service (Entra ID or Okta)',
      'Conditional access policies',
      'Two AI workflows tied to real bottlenecks',
      'Documentation + team training'
    ],
    featured: true
  },
  {
    name: '90-day Transformation',
    price: '€28,000',
    for: '50–100 person firms ready for a full ops overhaul.',
    includes: [
      'Everything in Modernize',
      '2–3 AI/automation flows across business areas',
      'GDPR + compliance hardening',
      'Vendor consolidation review',
      '60-day post-launch support',
      'Optional fractional CIO continuation'
    ],
    featured: false
  }
];

const beyondSprint = [
  {
    title: 'Fractional CIO',
    pricing: 'From €5,000 / month',
    body: 'Ongoing strategic role for firms that need IT leadership without hiring a full-time CIO. Monthly retainer, quarterly roadmap, on-call when it matters.'
  },
  {
    title: 'Diagnostic Call',
    pricing: '€750 · one-off',
    body: '90-minute deep-dive on a specific problem — security audit, AI feasibility, migration planning. Comes with a written follow-up plan within 48 hours.'
  },
  {
    title: 'Custom Engagement',
    pricing: 'Quote on call',
    body: 'M&A IT integration, complex multi-tenant migrations, or something I haven’t shipped before. We talk first, scope second, fixed price third.'
  }
];

const proof = [
  {
    snippet: 'A 22-person Sofia-based consultancy moved from Gmail + Dropbox + Slack to Microsoft 365 with secure document workflows in 30 days. The founder stopped being their own IT helpdesk.',
    tag: '30-day Quick Start'
  },
  {
    snippet: 'A boutique law firm modernized their identity stack with Entra ID and conditional access, eliminating five separate logins per matter. Partners noticed the friction disappear before they noticed the project finish.',
    tag: '60-day Modernize'
  },
  {
    snippet: 'A 65-person agency consolidated three overlapping SaaS contracts, cut their bill by ~30%, and shipped two AI workflows (proposal drafting + meeting summaries) in 90 days.',
    tag: '90-day Transformation'
  }
];

const faqs = [
  {
    q: 'How is this different from a Big4 consultancy?',
    a: 'They sell strategy decks and 18-month transformation programmes. I sell working systems delivered in 30 to 90 days, by the person who actually builds them.'
  },
  {
    q: 'What if my company is bigger than 100 people?',
    a: 'The Sprint format scales to about 150 people. Above that, we’d structure it as a custom engagement — same approach, longer timeline, scoped per-project.'
  },
  {
    q: 'Do you offer ongoing support after the sprint?',
    a: 'Yes. Every sprint includes 30–60 days of post-launch support. Beyond that, the Fractional CIO retainer handles ongoing strategy and the inevitable “quick question” moments.'
  },
  {
    q: 'What if I already have most of this set up?',
    a: 'Then the Sprint probably isn’t the right fit. Start with a Diagnostic Call (€750) — I’ll review what you have and tell you whether you need anything at all.'
  },
  {
    q: 'Why fixed pricing?',
    a: 'Because hourly billing aligns me with your budget, not your outcome. Fixed price means I’m incentivised to finish, document, and hand off cleanly.'
  },
  {
    q: 'Do you handle GDPR / data residency / specific compliance?',
    a: 'Yes for GDPR and standard EU data residency — baked into the 60- and 90-day tiers. For regulated industries (finance, health), we’ll talk on the diagnostic call first.'
  }
];

const MWOPreview = () => {
  const b = getBrand('mwo');

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ─────────────── HERO ─────────────── */}
      <section style={{
        minHeight: '88vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '120px clamp(24px, 7vw, 120px) 100px',
        background: '#0D0F14',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* dot grid */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(0,188,212,0.06) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          WebkitMaskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 40%, transparent 100%)',
          maskImage: 'radial-gradient(ellipse 90% 90% at 50% 50%, black 40%, transparent 100%)'
        }} />
        {/* glow */}
        <div style={{
          position: 'absolute', top: '10%', right: '-5%',
          width: '480px', height: '480px', pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(0,188,212,0.05) 0%, transparent 70%)'
        }} />

        <div className="anim-slide-r" style={{
          width: '56px', height: '3px', background: '#00BCD4', marginBottom: '40px'
        }} />

        <p className="anim-fade-up" style={{
          fontFamily: b.fontDisplay,
          fontSize: '12px',
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: '#00BCD4',
          marginBottom: '28px',
          fontWeight: 700
        }}>
          Modern work for professional services firms
        </p>

        <h1 className="anim-fade-up delay-1" style={{
          fontFamily: b.fontDisplay,
          fontWeight: 800,
          fontSize: 'clamp(2.6rem, 7vw, 6rem)',
          lineHeight: 1.02,
          letterSpacing: '-0.025em',
          color: '#F4F1EB',
          maxWidth: '980px',
          marginBottom: '32px'
        }}>
          Modern work setup<br />in 30, 60, or 90 days.
        </h1>

        <p className="anim-fade-up delay-2" style={{
          fontFamily: b.fontBody,
          fontSize: 'clamp(1.05rem, 2vw, 1.3rem)',
          lineHeight: 1.7,
          color: 'rgba(244,241,235,0.78)',
          maxWidth: '600px',
          marginBottom: '52px'
        }}>
          I help consultancies, agencies, and law firms move to Microsoft 365 or Google Workspace,
          lock down security, and put AI to work — without the Big4 timeline or pricetag.
        </p>

        <div className="anim-fade-up delay-3" style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
          <a href={BOOKING_URL} className="btn-primary-mwo">Book a 30-min strategy call →</a>
          <a href="#sprint" style={{
            fontFamily: b.fontDisplay,
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'rgba(244,241,235,0.7)',
            textDecoration: 'none',
            transition: 'color 0.2s'
          }}
            onMouseEnter={e => e.currentTarget.style.color = '#F4F1EB'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(244,241,235,0.7)'}
          >
            See the offer →
          </a>
        </div>
      </section>

      {/* ─────────────── WHO THIS IS FOR ─────────────── */}
      <section style={{ padding: '100px clamp(24px, 7vw, 120px)', background: '#111318' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <p style={{
            fontFamily: b.fontDisplay,
            fontSize: '11px',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: '#00BCD4',
            marginBottom: '16px',
            fontWeight: 700
          }}>Who this is for</p>
          <h2 style={{
            fontFamily: b.fontDisplay,
            fontWeight: 700,
            fontSize: 'clamp(1.8rem, 4vw, 3rem)',
            color: '#F4F1EB',
            letterSpacing: '-0.025em',
            lineHeight: 1.1,
            marginBottom: '64px',
            maxWidth: '780px'
          }}>
            Three kinds of firms I do my best work with.
          </h2>

          <div className="grid-3" style={{ gap: '24px' }}>
            {audiences.map((a, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
                style={{
                  background: '#161920',
                  border: '1px solid rgba(255,255,255,0.07)',
                  padding: '36px 32px'
                }}>
                <p style={{
                  fontFamily: b.fontDisplay,
                  fontSize: '11px',
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  color: '#00BCD4',
                  fontWeight: 700,
                  marginBottom: '14px'
                }}>{a.size}</p>
                <h3 style={{
                  fontFamily: b.fontDisplay,
                  fontWeight: 700,
                  fontSize: b.fz.xl,
                  color: '#F4F1EB',
                  marginBottom: '16px',
                  letterSpacing: '-0.01em'
                }}>{a.title}</h3>
                <p style={{ fontSize: b.fz.base, lineHeight: 1.7, color: b.textMuted }}>
                  {a.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── THE SPRINT (HERO OFFER) ─────────────── */}
      <section id="sprint" style={{ padding: '100px clamp(24px, 7vw, 120px)', background: '#0D0F14' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '64px', maxWidth: '780px' }}>
            <p style={{
              fontFamily: b.fontDisplay,
              fontSize: '11px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: '#00BCD4',
              fontWeight: 700,
              marginBottom: '16px'
            }}>The Modern Work Sprint</p>
            <h2 style={{
              fontFamily: b.fontDisplay,
              fontWeight: 700,
              fontSize: 'clamp(1.8rem, 4vw, 3rem)',
              color: '#F4F1EB',
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
              marginBottom: '20px'
            }}>
              Three tiers. Fixed scope.<br />Fixed price.
            </h2>
            <p style={{ fontSize: b.fz.lg, lineHeight: 1.7, color: b.textMuted }}>
              Pick the tier that matches your urgency. Each one ends with you fully operational on modern work.
            </p>
          </div>

          <div className="grid-3" style={{ gap: '20px', alignItems: 'stretch' }}>
            {sprints.map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: i * 0.08 }} viewport={{ once: true }}
                style={{
                  background: s.featured ? '#161920' : '#0F1115',
                  border: s.featured ? '1px solid #00BCD4' : '1px solid rgba(255,255,255,0.07)',
                  padding: '40px 32px',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                {s.featured && (
                  <div style={{
                    position: 'absolute',
                    top: '-1px',
                    left: '0',
                    right: '0',
                    height: '3px',
                    background: '#00BCD4'
                  }} />
                )}
                {s.featured && (
                  <p style={{
                    fontFamily: b.fontDisplay,
                    fontSize: '10px',
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: '#00BCD4',
                    fontWeight: 700,
                    marginBottom: '16px'
                  }}>Most popular</p>
                )}
                <h3 style={{
                  fontFamily: b.fontDisplay,
                  fontWeight: 700,
                  fontSize: '1.6rem',
                  color: '#F4F1EB',
                  letterSpacing: '-0.01em',
                  marginBottom: '12px'
                }}>{s.name}</h3>
                <p style={{
                  fontFamily: b.fontDisplay,
                  fontSize: '2.4rem',
                  color: '#00BCD4',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  marginBottom: '20px',
                  lineHeight: 1
                }}>{s.price}</p>
                <p style={{
                  fontSize: b.fz.sm,
                  lineHeight: 1.65,
                  color: b.textMuted,
                  marginBottom: '28px',
                  fontStyle: 'italic'
                }}>{s.for}</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
                  {s.includes.map((inc, j) => (
                    <li key={j} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <FaCheck size={12} color="#00BCD4" style={{ flexShrink: 0, marginTop: '5px' }} />
                      <span style={{ fontSize: b.fz.sm, lineHeight: 1.6, color: '#F4F1EB' }}>{inc}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div style={{ marginTop: '48px', textAlign: 'center' }}>
            <a href={BOOKING_URL} className="btn-primary-mwo">Book a strategy call to scope your sprint →</a>
          </div>
        </div>
      </section>

      {/* ─────────────── BEYOND THE SPRINT ─────────────── */}
      <section style={{ padding: '100px clamp(24px, 7vw, 120px)', background: '#111318' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '64px', maxWidth: '780px' }}>
            <p style={{
              fontFamily: b.fontDisplay,
              fontSize: '11px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: '#00BCD4',
              fontWeight: 700,
              marginBottom: '16px'
            }}>Beyond the Sprint</p>
            <h2 style={{
              fontFamily: b.fontDisplay,
              fontWeight: 700,
              fontSize: 'clamp(1.8rem, 4vw, 3rem)',
              color: '#F4F1EB',
              letterSpacing: '-0.025em',
              lineHeight: 1.1
            }}>
              Other ways to work together.
            </h2>
          </div>

          <div className="grid-3" style={{ gap: '24px' }}>
            {beyondSprint.map((o, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
                style={{
                  background: '#0F1115',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderTop: '3px solid #00BCD4',
                  padding: '36px 32px'
                }}>
                <h3 style={{
                  fontFamily: b.fontDisplay,
                  fontWeight: 700,
                  fontSize: b.fz.xl,
                  color: '#F4F1EB',
                  marginBottom: '8px',
                  letterSpacing: '-0.01em'
                }}>{o.title}</h3>
                <p style={{
                  fontFamily: b.fontDisplay,
                  fontSize: b.fz.base,
                  color: '#00BCD4',
                  fontWeight: 700,
                  marginBottom: '18px'
                }}>{o.pricing}</p>
                <p style={{ fontSize: b.fz.base, lineHeight: 1.7, color: b.textMuted }}>
                  {o.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── PROOF / RESULTS ─────────────── */}
      <section style={{ padding: '100px clamp(24px, 7vw, 120px)', background: '#0D0F14' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '64px', maxWidth: '780px' }}>
            <p style={{
              fontFamily: b.fontDisplay,
              fontSize: '11px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: '#00BCD4',
              fontWeight: 700,
              marginBottom: '16px'
            }}>How this plays out</p>
            <h2 style={{
              fontFamily: b.fontDisplay,
              fontWeight: 700,
              fontSize: 'clamp(1.8rem, 4vw, 3rem)',
              color: '#F4F1EB',
              letterSpacing: '-0.025em',
              lineHeight: 1.1
            }}>
              Recent engagements.
            </h2>
          </div>

          <div className="grid-3" style={{ gap: '24px' }}>
            {proof.map((p, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
                style={{
                  background: '#161920',
                  padding: '36px 32px',
                  borderLeft: '2px solid #00BCD4'
                }}>
                <p style={{
                  fontFamily: b.fontDisplay,
                  fontSize: '10px',
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  color: '#00BCD4',
                  fontWeight: 700,
                  marginBottom: '20px'
                }}>{p.tag}</p>
                <p style={{
                  fontFamily: b.fontDisplay,
                  fontSize: b.fz.lg,
                  lineHeight: 1.5,
                  color: '#F4F1EB',
                  fontWeight: 600,
                  letterSpacing: '-0.005em'
                }}>
                  “{p.snippet}”
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── FAQ ─────────────── */}
      <section style={{ padding: '100px clamp(24px, 7vw, 120px)', background: '#111318' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <p style={{
            fontFamily: b.fontDisplay,
            fontSize: '11px',
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: '#00BCD4',
            fontWeight: 700,
            marginBottom: '16px'
          }}>FAQ</p>
          <h2 style={{
            fontFamily: b.fontDisplay,
            fontWeight: 700,
            fontSize: 'clamp(1.8rem, 4vw, 3rem)',
            color: '#F4F1EB',
            letterSpacing: '-0.025em',
            lineHeight: 1.1,
            marginBottom: '56px'
          }}>
            Questions buyers actually ask.
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {faqs.map((f, i) => (
              <div key={i} style={{
                padding: '28px 0',
                borderTop: '1px solid rgba(255,255,255,0.08)',
                ...(i === faqs.length - 1 ? { borderBottom: '1px solid rgba(255,255,255,0.08)' } : {})
              }}>
                <h3 style={{
                  fontFamily: b.fontDisplay,
                  fontWeight: 700,
                  fontSize: b.fz.lg,
                  color: '#F4F1EB',
                  marginBottom: '12px',
                  letterSpacing: '-0.005em'
                }}>{f.q}</h3>
                <p style={{
                  fontSize: b.fz.base,
                  lineHeight: 1.7,
                  color: b.textMuted,
                  maxWidth: '720px'
                }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── CLOSING CTA ─────────────── */}
      <section style={{ padding: '120px clamp(24px, 7vw, 120px)', background: '#0D0F14', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div style={{ width: '56px', height: '3px', background: '#00BCD4', marginBottom: '32px' }} />
          <h2 style={{
            fontFamily: b.fontDisplay,
            fontWeight: 800,
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            color: '#F4F1EB',
            letterSpacing: '-0.025em',
            lineHeight: 1.05,
            marginBottom: '24px'
          }}>
            Ready to modernize<br />your firm?
          </h2>
          <p style={{
            fontSize: b.fz.lg,
            lineHeight: 1.7,
            color: 'rgba(244,241,235,0.78)',
            marginBottom: '40px',
            maxWidth: '560px'
          }}>
            30 minutes. No PowerPoint. We'll walk through your situation and figure out if there's a fit.
          </p>
          <a href={BOOKING_URL} className="btn-primary-mwo">
            Book your strategy call <FaArrowRight size={11} style={{ marginLeft: '8px' }} />
          </a>
        </div>
      </section>

      {/* preview banner */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        background: '#00BCD4',
        color: '#0D0F14',
        padding: '10px 18px',
        fontFamily: b.fontDisplay,
        fontSize: '11px',
        fontWeight: 800,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        boxShadow: '0 8px 24px rgba(0,188,212,0.3)',
        zIndex: 100
      }}>
        Preview — not live
      </div>
    </div>
  );
};

export default MWOPreview;
